import { useState } from 'react';

// Identified-info card. Each row is tap-to-edit so a tech can correct an
// OCR misread (e.g. an O / 0 confusion in the model number) without having
// to re-photograph the tag. Model # and Serial # also get a one-tap Copy
// button so the values can be pasted into a parts-portal search.
//
// `onChange(field, newValue)` is awaited before exiting edit mode so the
// caller can write through to the server. The Result page swallows network
// errors and keeps the local edit, so this component will always resolve.
export default function ModelCard({ data, onChange }) {
  if (!data) return null;
  const editable = typeof onChange === 'function';

  const fields = [
    { key: 'model_number',     label: 'Model #',   value: data.model_number,                      mono: true,  big: true,  copyable: true },
    { key: 'brand',            label: 'Brand',     value: data.brand_matched || data.brand || '', mono: false, big: false, copyable: false },
    { key: 'appliance_type',   label: 'Type',      value: data.appliance_type,                    mono: false, big: false, copyable: false },
    { key: 'serial_number',    label: 'Serial #',  value: data.serial_number,                     mono: true,  big: false, copyable: true  },
    { key: 'manufacture_date', label: 'Mfg. Date', value: data.manufacture_date,                  mono: false, big: false, copyable: false },
  ];

  return (
    <div className="card">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-ink-400 text-xs font-mono uppercase tracking-widest">Identified</h2>
        {data.confidence && (
          <span className={`badge ${
            data.confidence === 'high' ? 'badge-pass' :
            data.confidence === 'medium' ? 'badge-marginal' : 'badge-fail'
          }`}>
            {data.confidence} confidence
          </span>
        )}
      </div>

      <dl className="space-y-3">
        {fields.map((f) => (
          <Row
            key={f.key}
            field={f}
            editable={editable}
            onChange={onChange}
          />
        ))}
      </dl>

      {editable && (
        <p className="text-ink-400 text-xs mt-4 leading-snug">
          Tap the pencil to fix anything misread. Changes save automatically.
        </p>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Row — handles its own editing state so the parent stays simple. Display
// mode shows the value plus optional Copy + Edit buttons. Edit mode shows an
// input with Save (✓) and Cancel (✕). Save on Enter, cancel on Escape.

function Row({ field, editable, onChange }) {
  const { key, label, value, mono, big, copyable } = field;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value || '');
  const [saving, setSaving]   = useState(false);

  function startEdit() {
    setDraft(value || '');
    setEditing(true);
  }

  async function save() {
    if (saving) return;
    const next = draft.trim();
    const current = (value || '').trim();
    if (next === current) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      // Pass null when the tech blanks the field so the DB stores NULL, not "".
      await onChange(key, next.length ? next : null);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  }

  function cancel() {
    setDraft(value || '');
    setEditing(false);
  }

  function handleKey(e) {
    if (e.key === 'Enter')   { e.preventDefault(); save(); }
    if (e.key === 'Escape')  { e.preventDefault(); cancel(); }
  }

  // Display & input share the same typography classes so the row doesn't
  // visually jump on edit/save.
  const valueClass = `${mono ? 'font-mono' : ''} ${big ? 'text-2xl font-bold tracking-tight' : 'text-tap'}`.trim();

  return (
    <div className="flex items-center gap-3">
      <dt className="w-24 shrink-0 text-ink-400 text-sm font-medium self-center">{label}</dt>
      <dd className="flex-1 min-w-0 flex items-center gap-1">
        {editing ? (
          <>
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={handleKey}
              disabled={saving}
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              aria-label={`Edit ${label}`}
              className={`flex-1 min-w-0 min-h-[44px] bg-ink-800 border border-mra-red/60 rounded-md px-2 py-1 text-white focus:outline-none focus:border-mra-red ${valueClass}`}
            />
            <IconButton
              onClick={save}
              disabled={saving}
              ariaLabel={saving ? 'Saving' : 'Save'}
              variant="confirm"
            >
              {saving ? <SpinnerIcon /> : <CheckIcon />}
            </IconButton>
            <IconButton
              onClick={cancel}
              disabled={saving}
              ariaLabel="Cancel"
              variant="ghost"
            >
              <XIcon />
            </IconButton>
          </>
        ) : (
          <>
            {/* break-all (not truncate) so a long model/serial wraps to a
                second line instead of being cut off with an ellipsis.
                Field has no whitespace to wrap on (e.g. WED7300DW0) so
                break-all is needed to break at character boundaries. */}
            <span className={`flex-1 min-w-0 text-white break-all ${valueClass}`}>
              {value || <span className="text-ink-400 italic font-sans text-sm">unreadable</span>}
            </span>
            {copyable && <CopyButton value={value} />}
            {editable && (
              <IconButton
                onClick={startEdit}
                ariaLabel={`Edit ${label}`}
                variant="ghost"
              >
                <PencilIcon />
              </IconButton>
            )}
          </>
        )}
      </dd>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Copy button — uses navigator.clipboard with a textarea fallback for older
// iPad Safari versions. Shows a brief "Copied" check so the tech gets
// feedback without a full toast system.

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;

  async function handleCopy() {
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }

  return (
    <IconButton
      onClick={handleCopy}
      ariaLabel={copied ? 'Copied to clipboard' : `Copy ${value}`}
      variant={copied ? 'confirm' : 'ghost'}
    >
      {copied ? <CheckIcon /> : <ClipboardIcon />}
    </IconButton>
  );
}

async function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to manual fallback */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Buttons & icons. Square, 44×44 minimum so they stay tappable on iPad even
// with gloves on. Inline SVGs to avoid pulling in another dep.

function IconButton({ onClick, disabled, ariaLabel, variant = 'ghost', children }) {
  const base = 'shrink-0 h-11 w-11 inline-flex items-center justify-center rounded-md transition active:scale-95';
  const variants = {
    ghost:   'text-ink-300 hover:text-white active:bg-ink-800',
    confirm: 'bg-pass/20 text-pass hover:bg-pass/30',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={`${base} ${variants[variant] || variants.ghost} disabled:opacity-50`}
    >
      {children}
    </button>
  );
}

function PencilIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function ClipboardIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="3" width="6" height="4" rx="1" />
      <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function SpinnerIcon() {
  return (
    <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
      <path d="M12 4a8 8 0 0 1 8 8" />
    </svg>
  );
}
